import React, { useEffect, useRef, useState } from 'react';
// Import the updated CSS file name
import './About.css';
import imgbg from "../Assets/11.png"
// Main component
const AboutUsEnhanced = () => {
    // Refs remain largely the same structure
    const sectionRefs = useRef([]);
    const countersRef = useRef([]);
    const whyUsListRef = useRef(null);
    // Add refs for elements needing individual animation triggers if necessary
    const serviceCardRefs = useRef([]);
    const locationCardRefs = useRef([]);
    const statItemRefs = useRef([]);


    // --- Intersection Observer Logic ---
    useEffect(() => {
        const observerCallback = (entries, observerInstance) => {
            entries.forEach((entry) => {
                const target = entry.target;
                if (entry.isIntersecting) {
                    target.classList.add('is-visible');

                    // Trigger counter animation
                    if (target.classList.contains('about-stat-number-animated')) {
                        const counterRefObj = countersRef.current.find(ref => ref?.current === target);
                        if (counterRefObj?.current && counterRefObj.current.dataset.animated !== 'true') {
                            const endValue = parseInt(counterRefObj.current.dataset.value || '0', 10);
                            const suffix = counterRefObj.current.dataset.suffix || '';
                            const prefix = counterRefObj.current.dataset.prefix || '';
                            animateValue(counterRefObj, 0, endValue, 2500, suffix, prefix); // Slightly longer duration
                        }
                        // Unobserve counters after they start animating
                         observerInstance.unobserve(target);
                    }
                    // Trigger staggered list item animation for "Why Us" reasons
                    else if (target === whyUsListRef.current) {
                        const listItems = whyUsListRef.current.querySelectorAll('.about-why-us-reasons li');
                        listItems.forEach((item, index) => {
                            // Delay based on index for staggered effect
                            item.style.setProperty('--item-delay', `${index * 0.12}s`); // Slightly increased stagger
                            item.classList.add('list-item-visible');
                        });
                        // Unobserve the list container after triggering item animations
                        observerInstance.unobserve(target);
                    }
                    // Unobserve general sections after first view for performance
                    // Keep observing sections containing elements that might need repeated observation if logic changes
                    else if (!target.classList.contains('about-stat-number-animated') && target !== whyUsListRef.current) {
                        // Check if it's a card container or other element we want to animate once
                         if(target.classList.contains('about-section') ||
                           target.classList.contains('about-service-card') ||
                           target.classList.contains('about-stat-item') ||
                           target.classList.contains('about-location-card')) {
                            observerInstance.unobserve(target);
                        }
                        // Potentially keep observing others if needed, but usually unobserving is best
                        // observerInstance.unobserve(target);
                    }
                }
                // No 'else' block: Animations trigger once when elements become visible.
            });
        };

        const observerOptions = {
            threshold: 0.1, // Element needs to be 10% visible
            // rootMargin: "0px 0px -50px 0px" // Optional: trigger slightly before fully in view
         };
        const observer = new IntersectionObserver(observerCallback, observerOptions);

        // Observe all collected refs
        sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
        countersRef.current.forEach((counterRef) => { if (counterRef?.current) observer.observe(counterRef.current); });
        if (whyUsListRef.current) observer.observe(whyUsListRef.current);
        serviceCardRefs.current.forEach(ref => { if (ref) observer.observe(ref); });
        locationCardRefs.current.forEach(ref => { if (ref) observer.observe(ref); });
        statItemRefs.current.forEach(ref => { if (ref) observer.observe(ref); });


        // Cleanup
        return () => {
            if (observer) {
                sectionRefs.current.forEach((ref) => { if (ref) observer.unobserve(ref); });
                countersRef.current.forEach((counterRef) => { if (counterRef?.current) observer.unobserve(counterRef.current); });
                if (whyUsListRef.current) observer.unobserve(whyUsListRef.current);
                serviceCardRefs.current.forEach(ref => { if (ref) observer.unobserve(ref); });
                locationCardRefs.current.forEach(ref => { if (ref) observer.unobserve(ref); });
                statItemRefs.current.forEach(ref => { if (ref) observer.unobserve(ref); });
            }
        };
    }, []); // Run once on mount

    // --- Counter Animation Function (Smoother Easing) ---
    const animateValue = (ref, start, end, duration, suffix = '', prefix = '') => {
        if (!ref || !ref.current || ref.current.dataset.animated === 'true') return;
        ref.current.dataset.animated = 'true';
        let startTimestamp = null;
        const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            // Ease-out cubic function for smoother acceleration/deceleration
            const easedProgress = 1 - Math.pow(1 - progress, 4); // Use power of 4 for more pronounced ease-out
            const currentVal = Math.floor(easedProgress * (end - start) + start);

            if (ref.current) {
                ref.current.textContent = prefix + currentVal.toLocaleString() + suffix;
            }

            if (progress < 1) {
                window.requestAnimationFrame(step);
            } else {
                if (ref.current) {
                    ref.current.textContent = prefix + end.toLocaleString() + suffix;
                }
            }
        };
        window.requestAnimationFrame(step);
    };

    // --- Ref Assignment Helpers ---
    const addToSectionRefs = (el) => { if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el); };
    const assignCounterRef = (el) => { if (el && !countersRef.current.some(ref => ref.current === el)) countersRef.current.push({ current: el }); };
    // Add helpers for other individually observed elements
    const addToServiceCardRefs = (el) => { if (el && !serviceCardRefs.current.includes(el)) serviceCardRefs.current.push(el); };
    const addToLocationCardRefs = (el) => { if (el && !locationCardRefs.current.includes(el)) locationCardRefs.current.push(el); };
    const addToStatItemRefs = (el) => { if (el && !statItemRefs.current.includes(el)) statItemRefs.current.push(el); };


    // --- Component Render ---
    return (
        // Main container with global background elements
        <div className="about-container">
            {/* Global Background Layers */}
            <div className="about-global-background">
                <div className="about-bg-layer about-layer-gradient"></div>
                <div className="about-bg-layer about-bg-noise"></div>
                <div className="about-bg-layer about-bg-aurora"></div>
                <div className="about-bg-layer about-bg-grid-pattern"></div>
            </div>

            {/* --- Section 1: Introduction --- */}
            <section ref={addToSectionRefs} className="about-section about-intro-section">
                 <div className="about-section-shape shape-intro-float"></div> {/* Floating background shape */}
                <div className="about-intro-content-wrapper">
                    {/* Animated heading with mask reveal */}
                    <div className="about-heading-mask">
                         <h1 className="about-heading about-heading-stroke">Architecting the Cliniqor</h1>
                    </div>
                     <div className="about-heading-mask" style={{ animationDelay: '0.15s' }}> {/* Stagger mask reveals */}
                         <h1 className="about-heading">of Indian Healthcare</h1>
                     </div>
                     {/* Animated subheading with mask reveal */}
                     <div className="about-subheading-mask" style={{ animationDelay: '0.3s' }}>
                        <p className="about-subheading">
                            Established 2019. Cliniqor Healthcare project and manpower solutions transcends consultancy; we are <strong>catalysts for transformation</strong>, engineering innovative manpower & infrastructure solutions for unparalleled healthcare excellence across India.
                        </p>
                    </div>
                </div>
                <div className="about-intro-grid">
                    {/* Content side */}
                    <div className="about-intro-content">
                         {/* Add mask reveal for this heading too */}
                         <div className="about-content-heading-mask">
                             <h3>Precision, Partnership, Progress</h3>
                         </div>
                        <p>
                        In the intricate ecosystem of modern healthcare, Cliniqor Healthcare project and manpower solutions delivers strategic clarity. We serve as a vital bridge — seamlessly connecting exceptional talent with growth-ready institutions, and engineering infrastructure that anticipates the demands of tomorrow’s care landscape.
                        </p>
                        <p>
                        Our approach is holistic and deeply customized, crafted to suit each partner’s unique environment. We leverage domain expertise to source skilled professionals, streamline equipment acquisition through our Equipment Nexus, and design cutting-edge healthcare facilities that support innovation and excellence.
                        </p>
                    </div>
                    {/* Enhanced visual container */}
                    <div className="about-intro-visual-container">
                        <div className="about-intro-visual-flare"></div> {/* Lens flare effect */}
                        <div className="about-intro-visual" style={{ backgroundImage: `url(${imgbg})` }}>
                             <div className="about-visual-overlay-grid"></div> {/* Grid overlay */}
                             <div className="about-visual-border-anim"></div> {/* Animated border */}
                             <div className="about-visual-inner-shape"></div> {/* Inner morphing shape */}
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Section 2: Core Services --- */}
            <section ref={addToSectionRefs} className="about-section about-services-section">
                 <div className="about-section-shape shape-service-flow"></div> {/* Flowing background shape */}
                {/* Heading reveals */}
                 <div className="about-heading-mask"><h2 className="about-heading">Integrated Service Ecosystem</h2></div>
                <div className="about-subheading-mask" style={{ animationDelay: '0.15s' }}><p className="about-subheading">A synergistic suite engineered for efficiency and elevated standards.</p></div>

                <div className="about-services-grid">
                     {[ // Service data array
                        { icon: '🧑‍⚕️', title: 'Skilled Healthcare Manpower', desc: 'Strategic talent acquisition; optimal skill/cultural fit.' },
                        { icon: '🔄', title: 'Medical Equipment Nexus', desc: 'Transparent platform for direct buy/sell, optimizing costs.' },
                        { icon: '🏗️', title: 'Future-Ready Infrastructure', desc: 'Designing robust colleges/hospitals, AMCs & facilities.' },
                        { icon: '👥', title: 'Total Manpower Ecosystems', desc: 'Managing complete staffing for peak operational performance.' },
                     ].map((service, index) => (
                        // Assign individual refs for observer + Add inline style for stagger delay
                        <div key={index} ref={addToServiceCardRefs} className="about-service-card" style={{'--card-index': index}}>
                            <div className="about-service-card-bg-pattern"></div> {/* Card background pattern */}
                            <div className="about-service-card-border"></div> {/* Animated border */}
                            <div className="about-service-card-shine"></div> {/* Hover shine */}
                            <div className="about-service-icon-wrapper">
                                <span className="about-service-icon">{service.icon}</span>
                                <span className="about-service-icon-glow"></span> {/* Icon glow effect */}
                            </div>
                            <div className="about-service-card-content">
                                <h4>{service.title}</h4>
                                <p>{service.desc}</p>
                            </div>
                             <div className="about-service-card-link-indicator">
                                <span>Explore</span> {/* Animated "Explore" text */}
                                {/* SVG can be animated too */}
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 11L11 1M11 1H1M11 1V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                        </div>
                     ))}
                </div>
            </section>

             {/* ==================================================== */}
            {/* === Section 3: Medical Profiles (KEEP EXACTLY) === */}
            {/* ==================================================== */}
            <section ref={addToSectionRefs} className="about-section about-profiles-section">
                 {/* Add masks here too */}
                <div className="about-heading-mask">
                    <h2 className="about-heading">Connecting Specialized Expertise</h2>
                 </div>
                <div className="about-subheading-mask" style={{ animationDelay: '0.15s' }}>
                    <p className="about-subheading">Our extensive network sources high-caliber professionals across a diverse spectrum of critical medical roles:</p>
                 </div>
                {/* --- START OF PRESERVED JSX --- */}
                <div className="about-profiles-marquee">
                    <ul className="about-profiles-list track-1">
                        {[ 'Dermatologist', 'Orthopedician', 'Physician', 'Anesthesiologist', 'Interventional Cardiologist', 'Emergency Medicine Specialist', 'Nephrologist', 'Gastroenterologist', 'Endocrinologist', 'ENT Surgeon', 'Laparoscopic Surgeon', 'Urologist'].map(p => <li key={p+1}>{p}</li>)}
                        {[ 'Dermatologist', 'Orthopedician', 'Physician', 'Anesthesiologist', 'Interventional Cardiologist', 'Emergency Medicine Specialist', 'Nephrologist', 'Gastroenterologist', 'Endocrinologist', 'ENT Surgeon', 'Laparoscopic Surgeon', 'Urologist'].map(p => <li key={p+1+12}>{p}</li>)}
                    </ul>
                     <ul className="about-profiles-list track-2">
                        {['Psychiatrist', 'Pulmonologist', 'Neurosurgeon', 'Pediatrician', 'Gastro Surgeon', 'Neurophysician', 'Radiologist', 'Oncologist', 'Critical Care Specialist', 'Rheumatologist', 'Plastic Surgeon', 'Vascular Surgeon'].map(p => <li key={p+2}>{p}</li>)}
                        { ['Psychiatrist', 'Pulmonologist', 'Neurosurgeon', 'Pediatrician', 'Gastro Surgeon', 'Neurophysician', 'Radiologist', 'Oncologist', 'Critical Care Specialist', 'Rheumatologist', 'Plastic Surgeon', 'Vascular Surgeon'].map(p => <li key={p+2+12}>{p}</li>)}
                    </ul>
                </div>
                 {/* --- END OF PRESERVED JSX --- */}
            </section>
            {/* ==================================================== */}
            {/* === END OF PRESERVED PROFILES SECTION ============ */}
            {/* ==================================================== */}


            {/* --- Section 4: Why Choose Us / Stats --- */}
            <section ref={addToSectionRefs} className="about-section about-why-us-section">
                 {/* Note: This section combines stats and reasons visually */}
                 <div className="about-why-us-split-container">
                    {/* Left Side: Content & Stats */}
                    <div className="about-why-us-content-stats">
                         <div className="about-heading-mask"><h2 className="about-heading">The Cliniqor Advantage</h2></div>
                         <div className="about-subheading-mask" style={{ animationDelay: '0.15s' }}><p className="about-subheading">Quantifiable results defining our strategic edge.</p></div>
                        <div className="about-why-us-grid">
                             {/* Refined Stat Items with Refs */}
                             <div ref={addToStatItemRefs} className="about-stat-item" style={{'--item-index': 0}}>
                                 <div className="about-stat-item-glow"></div>
                                 <div className="about-stat-item-icon-bg">📊</div>
                                 {/* Assign counter ref */}
                                 <span ref={assignCounterRef} className="about-stat-number-animated" data-value="1000" data-suffix="+" data-prefix="">0</span>
                                 <span className="about-stat-label"> Doctors Placed</span>
                             </div>
                             <div ref={addToStatItemRefs} className="about-stat-item" style={{'--item-index': 1}}>
                                <div className="about-stat-item-glow"></div>
                                <div className="about-stat-item-icon-bg">🏢</div>
                                 <span ref={assignCounterRef} className="about-stat-number-animated" data-value="400" data-suffix="+" data-prefix="">0</span>
                                 <span className="about-stat-label">Partner Institutions</span>
                             </div>
                             <div ref={addToStatItemRefs} className="about-stat-item" style={{'--item-index': 2}}>
                                <div className="about-stat-item-glow"></div>
                                <div className="about-stat-item-icon-bg">⏳</div>
                                 <span ref={assignCounterRef} className="about-stat-number-animated" data-value="6" data-suffix="+" data-prefix="">0</span>
                                 <span className="about-stat-label">Years Collective Insight</span>
                             </div>
                             <div ref={addToStatItemRefs} className="about-stat-item" style={{'--item-index': 3}}>
                                <div className="about-stat-item-glow"></div>
                                 <div className="about-stat-item-icon-bg">💰</div>
                                 <span ref={assignCounterRef} className="about-stat-number-animated" data-value="80" data-suffix="L+" data-prefix="₹">₹0</span>
                                 <span className="about-stat-label">Highest Package Facilitated</span>
                             </div>
                        </div>
                    </div>

                    {/* Right Side: Reasons List */}
                    {/* Add ref to the outer container for observer */}
                    <div ref={whyUsListRef} className="about-why-us-reasons">
                         <div className="about-why-us-reasons-bg-shape"></div> {/* Decorative shape */}
                        <div className="about-why-us-reasons-inner">
                             {/* Use a more engaging heading for the list */}
                             <div className="about-list-heading-mask">
                                <h4>Core Pillars of Partnership</h4>
                             </div>
                             {/* List items will be animated via CSS targeting .list-item-visible */}
                            <ul>
                                {/* Example list item structure (repeat for others) */}
                                <li> {/* class 'list-item-visible' added by observer */}
                                    <div className="about-reason-icon">🚀</div>
                                    <div className="about-reason-text"><strong>Placement Velocity:</strong> Accelerated, rigorous matching maximizes potential immediately.</div>
                                </li>
                                <li>
                                    <div className="about-reason-icon">🧠</div>
                                    <div className="about-reason-text"><strong>Hyper-Local Intelligence:</strong> Deep focus solely on the Indian healthcare ecosystem ensures relevant strategies.</div>
                                </li>
                                <li>
                                    <div className="about-reason-icon">🧬</div>
                                    <div className="about-reason-text"><strong>Tailored Architectures:</strong> Bespoke solutions, meticulously aligned with your unique context, challenges, and ambitions.</div>
                                </li>
                                <li>
                                    <div className="about-reason-icon">📈</div>
                                    <div className="about-reason-text"><strong>Proven Reliability:</strong> Transparent processes, measurable ROI, and a steadfast partner for critical needs.</div>
                                </li>
                                <li>
                                    <div className="about-reason-icon">💡</div>
                                    <div className="about-reason-text"><strong>Future-Forward Integration:</strong> Integrating best practices and tech for long-term resilience.</div>
                                </li>
                                {/* <li>
                                    <div className="about-reason-icon">🤝</div>
                                    <div className="about-reason-text"><strong>Synergistic Partnership:</strong> Your vision is our mission. We invest in collaboration for shared, lasting success.</div>
                                </li> */}
                            </ul>
                        </div>
                    </div>
                 </div> {/* End split container */}
            </section>

            {/* --- Section 5: Vision & Mission --- */}
            <section ref={addToSectionRefs} className="about-section about-vision-mission-section">
                 {/* Animated background elements */}
                 <div className="about-vision-shape shape-vm-1"></div>
                 <div className="about-vision-shape shape-vm-2"></div>
                 <div className="about-vision-shape shape-vm-3"></div>

                <div className="about-vision-mission-grid">
                    {/* Refined Vision/Mission Cards */}
                    <div className="about-vision-mission-card card-vision">
                         <div className="about-vm-card-bg"></div>
                         <div className="about-vm-card-border"></div>
                        <div className="about-vm-card-content">
                            <div className="about-vm-icon-wrapper icon-vision">🔭</div>
                            <h3>Our Vision</h3>
                            <p>At Cliniqor Healthcare, we envision becoming a pioneering force in shaping the future of the healthcare ecosystem — not only in India but across global markets. Our vision is to bridge critical gaps in healthcare delivery by seamlessly integrating skilled manpower, cutting-edge infrastructure, and strategic consultancy to build institutions that are resilient, future-ready, and patient-centric.</p>
                        </div>
                    </div>
                    <div className="about-vision-mission-card card-mission">
                         <div className="about-vm-card-bg"></div>
                         <div className="about-vm-card-border"></div>
                        <div className="about-vm-card-content">
                            <div className="about-vm-icon-wrapper icon-mission">🎯</div>
                             <h3>Our Mission</h3>
                            <p>At Cliniqor Healthcare, our mission is to deliver comprehensive, future-focused solutions that raise the bar for healthcare institutions across every dimension. We are dedicated to empowering healthcare providers by offering integrated services that span staffing excellence, manpower solutions, infrastructure development, and strategic consultancy — all tailored to meet the evolving demands of modern care.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Section 6: Our Locations --- */}
            <section ref={addToSectionRefs} className="about-section about-locations-section">
                <div className="about-heading-mask"><h2 className="about-heading">National Reach, Local Expertise</h2></div>
                <div className="about-subheading-mask" style={{ animationDelay: '0.15s' }}><p className="about-subheading">Strategically positioned hubs ensure seamless service across India.</p></div>
                <div className="about-locations-grid">
                     {/* Refined location cards with Refs */}
                
                    <div ref={addToLocationCardRefs} className="about-location-card" style={{'--card-index': 1}}>
                         <div className="about-location-card-bg-mesh"></div>
                         <div className="about-location-content">
                             <span className="about-location-pin">📍</span>
                            <strong>Registered Office (HQ)</strong>
                            <p>  Pune, Maharashtra – 411028</p>
                        </div>
                        <div className="about-location-card-border"></div>
                    </div>
                    <div ref={addToLocationCardRefs} className="about-location-card" style={{'--card-index': 0}}>
                        <div className="about-location-card-bg-mesh"></div>
                        <div className="about-location-content">
                             <span className="about-location-pin">📍</span>
                            <strong>Corporate Office</strong>
                            <p>Mumbai, Maharashtra - 400001</p>
                        </div>
                        <div className="about-location-card-border"></div>
                    </div>
                </div>
            </section>

            {/* --- Section 7: Call to Action --- */}
            <section ref={addToSectionRefs} className="about-section about-cta-section">
                {/* More dynamic background shapes */}
                <div className="about-cta-background-shapes">
                    <div className="about-cta-shape cta-s-1"></div>
                    <div className="about-cta-shape cta-s-2"></div>
                    <div className="about-cta-shape cta-s-3"></div>
                    <div className="about-cta-shape cta-s-4"></div> {/* Added more */}
                    <div className="about-cta-shape cta-s-5"></div>
                </div>
                <div className="about-cta-content">
                     {/* Add mask reveal */}
                     <div className="about-heading-mask">
                        <h3 className="about-cta-heading">Shape the Future of <span>Your Healthcare</span></h3>
                     </div>
                     <div className="about-subheading-mask" style={{ animationDelay: '0.15s' }}>
                        <p className="about-cta-text">
                            Partner with Cliniqor for transformative talent, optimized infrastructure, and strategic growth. Let's build the future, together.
                        </p>
                     </div>
                     {/* Enhanced button */}
                    <a href="/contact" className="about-cta-button">
                        <span className="about-cta-button-text">Initiate Consultation</span>
                        {/* Add multiple layers for complex hover effect */}
                        <span className="about-cta-button-bg-layer"></span>
                        <span className="about-cta-button-flare"></span>
                        <span className="about-cta-button-particles"></span>
                    </a>
                </div>
            </section>

        </div> // End about-container
    );
};

export default AboutUsEnhanced;